import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  TextField,
  ListItem,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { generatePath, useParams } from 'react-router';
import { debounce } from 'lodash';
import { PATH_WORKSPACE } from 'src/routes/paths';
import AvatarCell from '../TreincoTable/ColumnFormatters/AvatarCell';
import TextLink from '../TreincoTable/ColumnFormatters/TextLink';
import devicesQueries from '../../gql/queries/device';
import { Virtuoso } from 'react-virtuoso';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

const DeviceSearch = () => {
  const { workspaceId } = useParams();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const trimmedSearch = searchTerm.trim();
  const isSearchTermValid = trimmedSearch.length >= MIN_SEARCH_LENGTH;

  const [getWorkspaceVisibleDevices, { data, loading, error }] = useLazyQuery(
    devicesQueries.GET_DEVICES_SEARCH,
    {
      variables: {
        workspaceId,
        includeSharedWorkspaces: true,
      },
    }
  );

  const sanitizeSearchTerm = useCallback((term) => {
    if (!term) {
      return term;
    }

    return term.replace(/[%_]/g, '\\$&');
  }, []);

  const buildSearchFilter = useCallback(
    (term) => {
      if (!term) {
        return undefined;
      }

      const wildcardTerm = `%${term}%`;

      return {
        or: [
          {
            name: {
              iLike: wildcardTerm,
            },
          },
          {
            uniqueId: {
              iLike: wildcardTerm,
            },
          },
          {
            workspacePrefix: {
              iLike: wildcardTerm,
            },
          },
        ],
      };
    },
    [sanitizeSearchTerm]
  );

  const runSearch = useCallback(
    (term) => {
      getWorkspaceVisibleDevices({
        variables: {
          workspaceId,
          includeSharedWorkspaces: true,
          filter: buildSearchFilter(term),
        },
      });
    },
    [buildSearchFilter, getWorkspaceVisibleDevices, workspaceId]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        runSearch(term);
      }, SEARCH_DEBOUNCE_MS),
    [runSearch]
  );

  useEffect(() => {
    if (!isSearchTermValid) {
      debouncedSearch.cancel();
      return undefined;
    }

    debouncedSearch(trimmedSearch);

    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch, isSearchTermValid, trimmedSearch]);

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && isSearchTermValid) {
      debouncedSearch.flush();
    }
  };

  const getDeviceInitial = (device) => {
    if (device?.name) {
      return device.name.charAt(0);
    }

    if (device?.uniqueId) {
      return device.uniqueId.charAt(0);
    }

    return '?';
  };

  const results = isSearchTermValid
    ? data?.getDevicesByWorkspace?.devices ?? []
    : [];

  return (
    <Box
      sx={{
        width: '350px',
        height: '100%',
        maxWidth: '100%',
        padding: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <TextField
        fullWidth
        label={t('device_search.label')}
        variant="outlined"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        helperText={
          !isSearchTermValid && searchTerm
            ? t('device_search.minimum_length')
            : t('device_search.helper')
        }
      />
      <Box
        style={{
          padding: theme.spacing(1),
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.palette.divider}`,
          height: '100%',
        }}
      >
        <Virtuoso
          style={{
            flex: 1,
            height: '100%',
          }}
          data={results}
          itemContent={(index, device) => {
            const deviceProfilePath = generatePath(
              PATH_WORKSPACE.deviceProfile(false).root,
              {
                workspaceId,
                deviceId: device.id,
              }
            );
            const deviceTitle =
              device.name ??
              device.uniqueId ??
              t('device_search.unnamed_device');
            const prefixText = device?.workspacePrefix?.prefix ?? device.prefix;
            const subtitle = [prefixText, device.uniqueId]
              .filter(Boolean)
              .join(' - ');

            return (
              <ListItem disablePadding key={device.id}>
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                  }}
                >
                  <AvatarCell
                    profileStyle={{ padding: 0 }}
                    name={getDeviceInitial(device)}
                    titleComponent={
                      <TextLink
                        text={deviceTitle}
                        to={deviceProfilePath}
                        variant="subtitle2"
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    }
                    subtitle={subtitle}
                    avatarSource={device.image ?? ''}
                  />
                </Box>
              </ListItem>
            );
          }}
          components={{
            EmptyPlaceholder: () => (
              <>
                {!searchTerm && (
                  <ListItem>
                    <ListItemText primary={t('device_search.start_typing')} />
                  </ListItem>
                )}
                {searchTerm && !isSearchTermValid && (
                  <ListItem>
                    <ListItemText primary={t('device_search.minimum_length')} />
                  </ListItem>
                )}
                {results.length === 0 && !loading && isSearchTermValid && (
                  <ListItem>
                    <ListItemText primary={t('device_search.empty')} />
                  </ListItem>
                )}
                {loading && (
                  <ListItem>
                    <ListItemText primary={t('device_search.loading')} />
                  </ListItem>
                )}
                {error && (
                  <ListItem>
                    <ListItemText primary={t('device_search.error')} />
                  </ListItem>
                )}
              </>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default DeviceSearch;
